import { logger } from 'src/utils/logger';
import { getCognitoAuthSession } from 'src/authentication/cognito';
import { SNOW_CHAT_API_ENDPOINT } from 'src/config';

const CHAT_API_URL = SNOW_CHAT_API_ENDPOINT;

enum SSEEventType {
	DATA = 'data',
	ERROR = 'error',
	DONE = 'done',
	SESSION = 'session',
}

interface SSEEvent {
	type: SSEEventType | string;
	data: string;
}

export enum AgentErrorCode {
	UNAVAILABLE = 'UNAVAILABLE',
	AUTH_FAILED = 'AUTH_FAILED',
	STREAM_ERROR = 'STREAM_ERROR',
	REQUEST_FAILED = 'REQUEST_FAILED',
	NO_STREAMING_SUPPORT = 'NO_STREAMING_SUPPORT',
}

export class AgentServiceError extends Error {
	public readonly code: AgentErrorCode;
	public readonly userMessage: string;
	public readonly details?: string;

	constructor(code: AgentErrorCode, userMessage: string, details?: string) {
		super(userMessage);
		this.name = 'AgentServiceError';
		this.code = code;
		this.userMessage = userMessage;
		this.details = details;
	}
}

export class SSEParser {
	private buffer = '';

	parse(chunk: string): SSEEvent[] {
		this.buffer += chunk;
		const results: SSEEvent[] = [];

		const segments = this.buffer.split('\n\n');
		this.buffer = segments.pop() || '';

		for (const segment of segments) {
			if (!segment.trim()) continue;
			const event = this.parseSegment(segment);
			if (event) results.push(event);
		}

		return results;
	}

	flush(): SSEEvent | null {
		if (!this.buffer.trim()) return null;
		const event = this.parseSegment(this.buffer);
		this.buffer = '';
		return event;
	}

	private parseSegment(segment: string): SSEEvent | null {
		const lines = segment.split('\n');
		let eventType = '';
		let data = '';

		for (const line of lines) {
			if (line.startsWith('event:')) {
				eventType = line.substring(6).trim();
			} else if (line.startsWith('data:')) {
				data = line.substring(5).trim();
			}
		}

		if (!eventType && !data) return null;

		return {
			type: eventType || SSEEventType.DATA,
			data,
		};
	}
}

export function generateSessionId(cognitoUsername: string): string {
	const uuid = crypto.randomUUID();
	return `${cognitoUsername}_${uuid}`;
}

export async function invokeAgent(params: {
	message: string;
	sessionId: string;
	actorId: string;
	onToken?: (token: string) => void;
	abortSignal?: AbortSignal;
}): Promise<{ text: string; sessionId: string; actorId: string }> {
	const { message, sessionId, actorId, onToken, abortSignal } = params;

	logger.info('Invoking AgentCore via API Gateway', { sessionId, actorId });

	if (!CHAT_API_URL) {
		throw new AgentServiceError(
			AgentErrorCode.UNAVAILABLE,
			'Workflow Assistant is not available in this environment.',
		);
	}

	let idToken: string | undefined;
	try {
		const session = await getCognitoAuthSession();
		idToken = (session as any).getIdToken?.()?.getJwtToken?.();
		if (!idToken) {
			logger.warn('No ID token available — request may fail auth');
		}
	} catch (err) {
		logger.error('Failed to get Cognito token', { error: err });
		throw new AgentServiceError(
			AgentErrorCode.AUTH_FAILED,
			'Unable to authenticate. Please refresh the page and try again.',
			String(err),
		);
	}

	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	if (idToken) {
		headers['Authorization'] = idToken;
	}

	const response = await fetch(CHAT_API_URL, {
		method: 'POST',
		headers,
		body: JSON.stringify({ message, sessionId, actorId }),
		signal: abortSignal,
	});

	if (!response.ok) {
		const errorText = await response.text();
		logger.error('AgentCore error response', { status: response.status, errorText });
		throw new AgentServiceError(
			AgentErrorCode.REQUEST_FAILED,
			'Something went wrong while processing your request. Please try again.',
			`HTTP ${response.status}: ${errorText}`,
		);
	}

	if (!response.body) {
		throw new AgentServiceError(
			AgentErrorCode.NO_STREAMING_SUPPORT,
			'Your browser does not support streaming responses. Please try a different browser.',
		);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	const parser = new SSEParser();
	let fullText = '';
	let streamDone = false;

	while (!streamDone) {
		const { done, value } = await reader.read();
		if (done) break;

		const chunk = decoder.decode(value, { stream: true });
		const events = parser.parse(chunk);

		for (const event of events) {
			switch (event.type) {
				case SSEEventType.ERROR: {
					let errorMsg = event.data;
					try {
						const parsed = JSON.parse(event.data);
						errorMsg = parsed.error || errorMsg;
					} catch { /* use raw data */ }
					logger.error('SSE stream error event', { errorMsg });
					throw new AgentServiceError(
						AgentErrorCode.STREAM_ERROR,
						'The assistant encountered an issue. Please try again.',
						errorMsg,
					);
				}

				case SSEEventType.DONE:
					streamDone = true;
					break;

				case SSEEventType.SESSION:
					break;

				default: {
					if (event.data) {
						const text = extractText(event.data);
						if (text) {
							fullText += text;
							if (onToken) onToken(text);
						}
					}
					break;
				}
			}

			if (streamDone) break;
		}
	}

	const remaining = parser.flush();
	if (remaining && remaining.data) {
		const text = extractText(remaining.data);
		if (text) {
			fullText += text;
			if (onToken) onToken(text);
		}
	}

	logger.info('AgentCore stream complete', { textLength: fullText.length });
	return { text: fullText, sessionId, actorId };
}

function extractText(data: string): string {
	try {
		const parsed = JSON.parse(data);

		if (parsed.event?.contentBlockDelta?.delta?.text) {
			return parsed.event.contentBlockDelta.delta.text;
		}

		if (parsed.message?.content) return '';

		if (
			parsed.init_event_loop ||
			parsed.start ||
			parsed.start_event_loop ||
			parsed.event?.messageStart ||
			parsed.event?.messageStop ||
			parsed.event?.contentBlockStop ||
			parsed.event?.metadata ||
			parsed.result
		) {
			return '';
		}

		return (
			parsed.text ||
			parsed.content ||
			parsed.delta?.text ||
			parsed.delta?.content ||
			parsed.completion ||
			''
		);
	} catch {
		if (data.startsWith("{'") || data.startsWith("\"{'")) return '';
		if (data && data !== '[DONE]') return data;
		return '';
	}
}
